<template>
    <div
      v-if="!isRippleAdmin && developerMode == false"
      class="sales-dashboard"
      :class="{ 'is-fullscreen': isFullscreen }"
    >
      <base-header type="pattern" class="pb-6 pb-8 pt-5 pt-md-8">
        <!-- Page heading -->
        <div class="page-heading mb-4">
          <div class="page-heading-text">
            <h1 class="page-title">Sales Dashboard</h1>
            <p class="page-subtitle">Sales performance for the current month. Filter by brand to narrow results.</p>
          </div>
          <div class="page-heading-actions">
            <a-select v-model="autoRefreshMinutes" style="width: 180px" @change="onAutoRefreshChange">
              <a-select-option v-for="opt in autoRefreshOptions" :key="opt" :value="opt">
                Refresh every {{ opt }} min
              </a-select-option>
            </a-select>
            <a-button class="fullscreen-toggle" @click="toggleFullscreen">
              <i :class="isFullscreen ? 'fas fa-compress' : 'fas fa-expand'" aria-hidden="true"></i>
              {{ isFullscreen ? 'Exit full screen' : 'Full screen' }}
            </a-button>
          </div>
        </div>

        <!-- Filter bar -->
        <div class="dash-card filter-card mb-4">
          <div class="filter-bar">
            <div class="filter-field">
              <label class="filter-label">Brand</label>
              <a-select
                showSearch
                mode="multiple"
                v-model="brandFilter"
                optionFilterProp="children"
                style="width: 100%"
                :filterOption="filterOption"
                placeholder="All Brands"
                allowClear
              >
                <a-select-option v-for="brandOption in filterBrandOptions" :key="brandOption.id" :value="brandOption.id">{{ brandOption.name }}</a-select-option>
              </a-select>
            </div>
            <div class="filter-actions">
              <a-button type="primary" @click="applyFilter()">Apply Filter</a-button>
              <a-button @click="clearFilter()">Clear Filter</a-button>
            </div>
          </div>
        </div>

        <!-- KPI cards -->
        <div class="kpi-grid mb-4">
          <a-spin :spinning="spinner.month_to_date_sales">
            <div class="kpi-card">
              <div class="kpi-icon kpi-icon-green"><i class="fa-solid fa-rotate-right" aria-hidden="true"></i></div>
              <div class="kpi-body">
                <span class="kpi-label">Sales Month-to-Date</span>
                <span class="kpi-value">{{ dashboard.total_month_to_date_sales }}</span>
              </div>
            </div>
          </a-spin>

          <a-spin :spinning="spinner.sales">
            <div class="kpi-card">
              <div class="kpi-icon kpi-icon-blue"><i class="fas fa-shopping-cart" aria-hidden="true"></i></div>
              <div class="kpi-body">
                <span class="kpi-label">Sales Today</span>
                <span class="kpi-value">{{ dashboard.total_sales }}</span>
              </div>
            </div>
          </a-spin>

          <a-spin :spinning="spinner.yesterday_sales">
            <div class="kpi-card">
              <div class="kpi-icon kpi-icon-indigo"><i class="fa-solid fa-backward" aria-hidden="true"></i></div>
              <div class="kpi-body">
                <span class="kpi-label">Sales Yesterday</span>
                <span class="kpi-value">{{ dashboard.total_yesterday_sales }}</span>
              </div>
            </div>
          </a-spin>

          <a-spin :spinning="spinner.last7days_sales">
            <div class="kpi-card">
              <div class="kpi-icon kpi-icon-orange"><i class="fa-solid fa-7" aria-hidden="true"></i></div>
              <div class="kpi-body">
                <span class="kpi-label">Sales Ave. Past 7 Days</span>
                <span class="kpi-value">{{ dashboard.total_last7days_sales }}</span>
              </div>
            </div>
          </a-spin>

          <a-spin :spinning="spinner.month_ave_sales">
            <div class="kpi-card">
              <div class="kpi-icon kpi-icon-teal"><i class="fa-solid fa-gauge-simple" aria-hidden="true"></i></div>
              <div class="kpi-body">
                <span class="kpi-label">Sales Ave. Month</span>
                <span class="kpi-value">{{ dashboard.total_month_ave_sales }}</span>
              </div>
            </div>
          </a-spin>

          <a-spin :spinning="spinner.averageBasketSize">
            <div class="kpi-card">
              <div class="kpi-icon kpi-icon-pink"><i class="fa-solid fa-shopping-basket" aria-hidden="true"></i></div>
              <div class="kpi-body">
                <span class="kpi-label">Average Basket Size</span>
                <span class="kpi-value">{{ averageBasketSize }}</span>
              </div>
            </div>
          </a-spin>
        </div>

        <!-- Sales trend chart -->
        <div class="dash-card mb-4">
          <div class="dash-card-header">
            <h2 class="dash-card-title">Sales Trend</h2>
            <span class="dash-card-subtitle">Last 7 days</span>
          </div>
          <div class="dash-card-body">
            <a-spin :spinning="spinner.line_sales">
              <div class="chart-wrap">
                <canvas id="myChart"></canvas>
              </div>
            </a-spin>
          </div>
        </div>

        <!-- Tables -->
        <div class="table-grid">
          <!-- TOP SKUs -->
          <div class="dash-card">
            <div class="dash-card-header">
              <h2 class="dash-card-title">Top SKUs</h2>
            </div>
            <a-spin :spinning="spinner.top_items">
              <div class="table-responsive">
                <table class="dash-table">
                  <thead>
                    <tr>
                      <th class="text-center">#</th>
                      <th class="text-center">Barcode</th>
                      <th>Name</th>
                      <th class="text-right">Quantity</th>
                      <th class="text-right">Sales</th>
                      <th class="text-right">Onhand Qty.</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in dashboard.top_items" :key="item.row_number">
                      <td class="text-center text-muted">{{ item.row_number }}</td>
                      <td class="text-center">{{ item.barcode }}</td>
                      <td>{{ item.product_name }}</td>
                      <td class="text-right">{{ convertToCurrency(Number(item.quantity)) }}</td>
                      <td class="text-right font-weight-bold">{{ convertToCurrency(Number(item.amount)) }}</td>
                      <td class="text-right">{{ convertToCurrency(Number(item.onhand_quantity)) }}</td>
                    </tr>
                    <tr v-if="!dashboard.top_items.length">
                      <td colspan="6" class="empty-row">No data available</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </a-spin>
          </div>

          <!-- TOP Branches -->
          <div class="dash-card">
            <div class="dash-card-header">
              <h2 class="dash-card-title">Top Branches</h2>
            </div>
            <a-spin :spinning="spinner.top_sites">
              <div class="table-responsive">
                <table class="dash-table">
                  <thead>
                    <tr>
                      <th class="text-center">#</th>
                      <th class="text-center">Code</th>
                      <th>Name</th>
                      <th class="text-right">Sales</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="site in dashboard.top_sites" :key="site.row_number">
                      <td class="text-center text-muted">{{ site.row_number }}</td>
                      <td class="text-center">{{ site.refcode }}</td>
                      <td>{{ site.name }}</td>
                      <td class="text-right font-weight-bold">{{ convertToCurrency(Number(site.amount)) }}</td>
                    </tr>
                    <tr v-if="!dashboard.top_sites.length">
                      <td colspan="4" class="empty-row">No data available</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </a-spin>
          </div>
        </div>
      </base-header>
    </div> <!-- end condition for user type-->
  </template>
    
  <script>
    import secureStorage from '../controllers/secureStorage.js'
    import HTTPRequest from '../controllers/httpRequest.js'
    import moment from 'moment';
    import fullscreenToggle from '../mixins/fullscreenToggle'
    import autoRefresh from '../mixins/autoRefresh'
  
    var Chart = require('chart.js');
    // eslint-disable-next-line no-unused-vars
    var myChart
    var component_x
    import {
      mapState, mapActions
    } from 'vuex'
  
    export default {
      mixins: [fullscreenToggle, autoRefresh],
      computed: {
        ...mapState(['loggedIn', 'endPoint', 'serverType']),
      },
      data() {
        return {
          brandFilter:[],
          developerMode: secureStorage.getItem("developerMode"),
          site_type: 'ho',
          dashboard: {
            total_sales: "0",
            total_last7days_sales: "0",
            total_yesterday_sales: "0",
            total_month_to_date_sales: "0",
            total_month_to_date_profit: "0",
            total_last7_sales: "0",
            total_month_ave_sales: "0",
            top_items: [],
            top_sites: []
          },
          filterBrandOptions: [],
          spinner: {
            sales: false,
            month_to_date_sales: false,
            month_to_date_profit: false,
            last7days_sales: false,
            yesterday_sales: false,
            month_ave_sales: false,
            top_items: false,
            top_sites: false,
            line_sales: false,
            averageBasketSize: false
          },
          averageBasketSize: 0,
          chartOptions: {
            type: 'line',
            data: {
              labels: ['Feb 22', 'Feb 23', 'Feb 24', 'Feb 25', 'Feb 26', 'Feb 27', 'Feb 28'],
              datasets: [{
                label: 'Sales',
                data: [1, 1, 1, 1, 1, 1, 1],
                backgroundColor: '#2dce89',
                borderColor: 'rgba(255, 99, 132, 1)',
                borderWidth: 1
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false
            }
          },
          isRippleAdmin: secureStorage.getItem("isRippleAdmin")
        };
      },
      mounted: async function () {
        component_x = this
  
        if (this.loggedIn == false && this.developerMode == false) {
          this.$router.push('/')
        } else {
          await this.getBrandOptions();
          await this.applyFilter();
          this.startAutoRefresh();
          // end of top branches
        }
      },
      methods: {
        ...mapActions(['updateCurrentProfile']),
        moment,
        // Auto-refresh hook (see mixins/autoRefresh.js): re-run the current
        // filter so the user's brand selection is kept.
        refreshDashboard() {
          return this.applyFilter()
        },
        convertToCurrency(num){
          return num.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
        },
        filterOption(input, option) {
          return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0
        },
        setCurrentProfile(profile) {
          this.updateCurrentProfile(profile);
        },
        async getBrandOptions() {
          const brands = await HTTPRequest.getRequest(this.endPoint + "/brands/get_valid_brands?mode=all&userBrands=true", null, null);
          this.filterBrandOptions = brands.map(brand => ({ id: brand.id, name: brand.name }));
        },
        async clearFilter(){
          this.brandFilter = []
        },
        async applyFilter(){
          Object.assign(component_x.spinner, {
            sales: true,
            month_to_date_sales: true,
            month_to_date_profit: true,
            last7days_sales: true,
            yesterday_sales: true,
            month_ave_sales: true,
            top_items: true,
            top_sites: true,
            line_sales: true,
            averageBasketSize: true
          })
          var add_brand_filter = ""
          if (this.brandFilter.length > 0){
            add_brand_filter = "&brand_ids="+this.brandFilter.join();
          }
          let siteId = secureStorage.getItem("defaultSiteId")
          let siteConceptId = secureStorage.getItem("defaultSiteConceptId")
          let site = await HTTPRequest.getRequest(this.endPoint + "/sites/" + siteId, null)
          if(site != undefined){
              component_x.site_type = site.site_type
          }
          const current_date = new Date().setDate(new Date().getDate());
          const prevday7 = new Date(new Date().setDate(new Date().getDate()-7));
          const prevday6 = new Date(new Date().setDate(new Date().getDate()-6));
          const prevday5 = new Date(new Date().setDate(new Date().getDate()-5));
          const prevday4 = new Date(new Date().setDate(new Date().getDate()-4));
          const prevday3 = new Date(new Date().setDate(new Date().getDate()-3));
          const prevday2 = new Date(new Date().setDate(new Date().getDate()-2));
          const yesterday = new Date(new Date().setDate(new Date().getDate()-1));
  
          let formatted_current_date = moment(current_date).format('YYYY-MM-DD');
          let formatted_firstDay = moment().startOf('month').format('YYYY-MM-DD');
          let formatted_yesterday= moment(yesterday).format('YYYY-MM-DD');
          let formatted_prev2 = moment(prevday2).format('YYYY-MM-DD');
          let formatted_prev3 = moment(prevday3).format('YYYY-MM-DD');
          let formatted_prev4 = moment(prevday4).format('YYYY-MM-DD');
          let formatted_prev5 = moment(prevday5).format('YYYY-MM-DD');
          let formatted_prev6 = moment(prevday6).format('YYYY-MM-DD');
          let formatted_prev7 = moment(prevday7).format('YYYY-MM-DD');
  
          let formatted_label_yesterday = moment(yesterday).format('MMM DD');
          let formatted_label_prev2 = moment(prevday2).format('MMM DD');
          let formatted_label_prev3 = moment(prevday3).format('MMM DD');
          let formatted_label_prev4 = moment(prevday4).format('MMM DD');
          let formatted_label_prev5 = moment(prevday5).format('MMM DD');
          let formatted_label_prev6 = moment(prevday6).format('MMM DD');
          let formatted_label_prev7 = moment(prevday7).format('MMM DD');
  
          // month to date sales
          let month_to_date_sales_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            month_to_date_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?site_id=' + siteId + '&date_from=' + formatted_firstDay + '&date_to=' + formatted_current_date + add_brand_filter, null)
          } else {
            month_to_date_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?date_from=' + formatted_firstDay + '&date_to=' + formatted_current_date  + add_brand_filter, null)
          }
  
          if(month_to_date_sales_data != undefined){
            let total_month_to_date_sales = component_x.convertToCurrency(Number(month_to_date_sales_data.total_sales) - Number(month_to_date_sales_data.total_returns))
            
            Object.assign(component_x.dashboard, {
              total_month_to_date_sales: total_month_to_date_sales
            })
          }
          component_x.spinner.month_to_date_sales = false
          // end of month to date sales
  
          // month to date profit
          let month_to_date_profit_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            month_to_date_profit_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_profit_summary?site_id=' + siteId + '&date_from=' + formatted_firstDay + '&date_to=' + formatted_current_date + add_brand_filter, null)
          } else {
            month_to_date_profit_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_profit_summary?date_from=' + formatted_firstDay + '&date_to=' + formatted_current_date + add_brand_filter, null)
          }
  
          if(month_to_date_profit_data != undefined){
            let total_month_to_date_profit = component_x.convertToCurrency(Number(month_to_date_profit_data.total_sales) - Number(month_to_date_profit_data.total_returns))
  
            Object.assign(component_x.dashboard, {
              total_month_to_date_profit: total_month_to_date_profit
            })
          }
          component_x.spinner.month_to_date_profit = false
          // end of month to date profit
  
          //basket
          let month_to_date_basket_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            month_to_date_basket_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/get_average_basket_size?site_id=' + siteId + '&date_from=' + formatted_firstDay + '&date_to=' + formatted_current_date + add_brand_filter, null)
          } else {
            month_to_date_basket_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/get_average_basket_size?date_from=' + formatted_firstDay + '&date_to=' + formatted_current_date + add_brand_filter, null)
          }
          if (month_to_date_basket_data != undefined){
              this.averageBasketSize = component_x.convertToCurrency(Number(month_to_date_basket_data.average_basket_size))
          } 
          component_x.spinner.averageBasketSize = false
          //end of basket
  
          // yesterday sales
          let yesterday_sales_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            yesterday_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?site_id=' + siteId + '&date_from=' + formatted_yesterday + '&date_to=' + formatted_yesterday + add_brand_filter, null)
          } else {
            yesterday_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?date_from=' + formatted_yesterday + '&date_to=' + formatted_yesterday + add_brand_filter, null)
          }
          let total_yesterday_sales = 0
          if(yesterday_sales_data != undefined){
            total_yesterday_sales = Number(yesterday_sales_data.total_sales) - Number(yesterday_sales_data.total_returns)
  
            Object.assign(component_x.dashboard, {
              total_yesterday_sales: component_x.convertToCurrency(total_yesterday_sales)
            })
          }
          // end of yesterday sales
  
          // line graph
          let prev2_sales_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            prev2_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?site_id=' + siteId + '&date_from=' + formatted_prev2 + '&date_to=' + formatted_prev2 + add_brand_filter, null)
          } else {
            prev2_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?date_from=' + formatted_prev2 + '&date_to=' + formatted_prev2 + add_brand_filter, null)
          }
          let prev2_sales = 0
          if(prev2_sales_data != undefined){
            prev2_sales = Number(prev2_sales_data.total_sales) - Number(prev2_sales_data.total_returns)
          }
  
          let prev3_sales_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            prev3_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?site_id=' + siteId + '&date_from=' + formatted_prev3 + '&date_to=' + formatted_prev3 + add_brand_filter, null)
          } else {
            prev3_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?date_from=' + formatted_prev3 + '&date_to=' + formatted_prev3 + add_brand_filter, null)
          }
          let prev3_sales = 0
          if(prev3_sales_data != undefined){
            prev3_sales = Number(prev3_sales_data.total_sales) - Number(prev3_sales_data.total_returns)
          }
  
          let prev4_sales_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            prev4_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?site_id=' + siteId + '&date_from=' + formatted_prev4 + '&date_to=' + formatted_prev4 + add_brand_filter, null)
          } else {
            prev4_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?date_from=' + formatted_prev4 + '&date_to=' + formatted_prev4 + add_brand_filter, null)
          }
          let prev4_sales = 0
          if(prev4_sales_data != undefined){
            prev4_sales = Number(prev4_sales_data.total_sales) - Number(prev4_sales_data.total_returns)
          }
  
          let prev5_sales_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            prev5_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?site_id=' + siteId + '&date_from=' + formatted_prev5 + '&date_to=' + formatted_prev5 + add_brand_filter, null)
          } else {
            prev5_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?date_from=' + formatted_prev5 + '&date_to=' + formatted_prev5 + add_brand_filter, null)
          }
          let prev5_sales = 0
          if(prev5_sales_data != undefined){
            prev5_sales = Number(prev5_sales_data.total_sales) - Number(prev5_sales_data.total_returns)
          }
  
          let prev6_sales_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            prev6_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?site_id=' + siteId + '&date_from=' + formatted_prev6 + '&date_to=' + formatted_prev6 + add_brand_filter, null)
          } else {
            prev6_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?date_from=' + formatted_prev6 + '&date_to=' + formatted_prev6 + add_brand_filter, null)
          }
          let prev6_sales = 0
          if(prev6_sales_data != undefined){
            prev6_sales = Number(prev6_sales_data.total_sales) - Number(prev6_sales_data.total_returns)
          }
  
          let prev7_sales_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            prev7_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?site_id=' + siteId + '&date_from=' + formatted_prev7 + '&date_to=' + formatted_prev7 + add_brand_filter, null)
          } else {
            prev7_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?date_from=' + formatted_prev7 + '&date_to=' + formatted_prev7 + add_brand_filter, null)
          }
          let prev7_sales = 0
          if(prev7_sales_data != undefined){
            prev7_sales = Number(prev7_sales_data.total_sales) - Number(prev7_sales_data.total_returns)
          }
          
          component_x.chartOptions.data.labels = [formatted_label_prev7, formatted_label_prev6, formatted_label_prev5, formatted_label_prev4, formatted_label_prev3, formatted_label_prev2, formatted_label_yesterday]
          component_x.chartOptions.data.datasets[0].data = [prev7_sales, prev6_sales, prev5_sales, prev4_sales, prev3_sales, prev2_sales, total_yesterday_sales]
          // Destroy the previous chart first: this runs on every filter apply
          // and auto-refresh, and Chart.js can't draw twice on the same canvas.
          if (myChart) {
            myChart.destroy();
          }
          myChart = new Chart("myChart", component_x.chartOptions);
          component_x.spinner.line_sales = false
          // end of line graph
  
          // sales for the day
          let sales_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/pos_document_summary?site_id=' + siteId + add_brand_filter, null)
          } else {
            sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/pos_document_summary?sample=true'+ add_brand_filter, null)
          }
  
          if(sales_data != undefined){
            let total_sales = component_x.convertToCurrency(Number(sales_data.total_sales) - Number(sales_data.total_returns))
  
            Object.assign(component_x.dashboard, {
              total_sales: total_sales
            })
          }
          component_x.spinner.sales = false
          // end of sales for the day
  
          // last 7 days sales
          let last7days_sales_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            last7days_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?site_id=' + siteId + '&date_from=' + formatted_prev7 + '&date_to=' + formatted_yesterday + add_brand_filter, null)
          } else {
            last7days_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?date_from=' + formatted_prev7 + '&date_to=' + formatted_yesterday + add_brand_filter, null)
          }
          let total_last7days_sales = 0
          if(last7days_sales_data != undefined){
            total_last7days_sales = Number(last7days_sales_data.total_sales) - Number(last7days_sales_data.total_returns)
            let ave7days = 0
            if (total_last7days_sales != 0){
              ave7days = (total_last7days_sales / 7)
            }
            
            Object.assign(component_x.dashboard, {
              total_last7days_sales: component_x.convertToCurrency(ave7days)
            })
          }
          component_x.spinner.last7days_sales = false
          // end of last 7 days sales
  
          // spinner for yesterday
          component_x.spinner.yesterday_sales = false
  
          // average month to yesterday sales
          let month_ave_sales_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            month_ave_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?site_id=' + siteId + '&date_from=' + formatted_firstDay + '&date_to=' + formatted_yesterday, null)
          } else {
            month_ave_sales_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_summary?date_from=' + formatted_firstDay + '&date_to=' + formatted_yesterday, null)
          }
  
          if(month_ave_sales_data != undefined){
            let total_month_ave_sales = Number(month_ave_sales_data.total_sales) - Number(month_ave_sales_data.total_returns)
            let ave_month_days = 0
            let day_to_denom = Number(moment(yesterday).format('D'));
            if (total_month_ave_sales != 0){
              ave_month_days = (total_month_ave_sales / day_to_denom)
            }
            
            Object.assign(component_x.dashboard, {
              total_month_ave_sales: component_x.convertToCurrency(ave_month_days)
            })
          }
          component_x.spinner.month_ave_sales = false
          // end of average month to yesterday sales
  
          let top_items_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            top_items_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_top_item_sales?top=10&site_id=' + siteId + '&date_from=' + formatted_firstDay + '&date_to=' + formatted_current_date + add_brand_filter, null)
          } else {
            top_items_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_top_item_sales?top=10&date_from=' + formatted_firstDay + '&date_to=' + formatted_current_date + add_brand_filter, null)
          }
  
          if(top_items_data != undefined){
            Object.assign(component_x.dashboard, {
              top_items: top_items_data
            })
          }
          component_x.spinner.top_items = false
  
          // top branches
          let top_sites_data = []
          if(['branch','BRANCH','Branch'].includes(component_x.site_type)){
            top_sites_data = top_sites_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_top_site_sales?top=10&site_concept_id='+siteConceptId+'&date_from=' + formatted_firstDay + '&date_to=' + formatted_current_date + add_brand_filter, null)
          } else {
            top_sites_data = top_sites_data = await HTTPRequest.getRequest(component_x.endPoint + 'pos_documents/range_top_site_sales?top=10&date_from=' + formatted_firstDay + '&date_to=' + formatted_current_date + add_brand_filter, null)
          }
          if(top_sites_data != undefined){
            Object.assign(component_x.dashboard, {
              top_sites: top_sites_data
            })
          }
          component_x.spinner.top_sites = false
        }

      }
    };
  </script>
  <style lang='scss'>
    .ant-spin-nested-loading,
    .ant-spin-container {
      height: 100%;
    }

    .sales-dashboard {
      .page-heading {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;

        .page-title {
          color: #fff;
          font-weight: 700;
          font-size: 1.75rem;
          margin-bottom: 0.25rem;
        }
        .page-subtitle {
          color: rgba(255, 255, 255, 0.8);
          font-size: 1rem;
          margin-bottom: 0;
        }
        .page-heading-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }
        .fullscreen-toggle i {
          margin-right: 6px;
        }
      }

      // Full screen: cover the sidebar / navbar. z-index stays below Ant
      // Design popups (1050) so dropdowns and date pickers still show on top.
      &.is-fullscreen {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 1000;
        overflow-y: auto;
        background: #f8f9fe;
      }

      // Filter bar card
      .filter-card .filter-bar {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        gap: 1rem;
        padding: 1.25rem 1.5rem;

        .filter-field {
          display: flex;
          flex-direction: column;
          flex: 1 1 320px;
          min-width: 220px;
        }
        .filter-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          color: #8898aa;
          margin-bottom: 0.35rem;
        }
        .filter-actions {
          display: flex;
          gap: 0.5rem;
          align-items: flex-end;
        }
      }

      // Responsive KPI card grid
      .kpi-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 1rem;
      }

      .kpi-card {
        display: flex;
        align-items: center;
        gap: 1rem;
        background: #fff;
        border-radius: 0.75rem;
        padding: 1.25rem;
        box-shadow: 0 0 2rem 0 rgba(136, 152, 170, 0.15);
        height: 100%;

        .kpi-icon {
          flex: 0 0 auto;
          width: 3rem;
          height: 3rem;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 1.1rem;
        }
        .kpi-icon-green  { background: linear-gradient(87deg, #2dce89, #2dcecc); }
        .kpi-icon-blue   { background: linear-gradient(87deg, #11cdef, #1171ef); }
        .kpi-icon-indigo { background: linear-gradient(87deg, #5e72e4, #825ee4); }
        .kpi-icon-orange { background: linear-gradient(87deg, #fb6340, #fbb140); }
        .kpi-icon-teal   { background: linear-gradient(87deg, #2dcecc, #11cdef); }
        .kpi-icon-pink   { background: linear-gradient(87deg, #f3a4b5, #f5365c); }

        .kpi-body {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }
        .kpi-label {
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #8898aa;
          margin-bottom: 0.25rem;
        }
        .kpi-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #32325d;
          line-height: 1.1;
        }
      }

      // Generic dashboard card used for chart + tables
      .dash-card {
        background: #fff;
        border-radius: 0.75rem;
        box-shadow: 0 0 2rem 0 rgba(136, 152, 170, 0.15);
        overflow: hidden;

        .dash-card-header {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid #e9ecef;
        }
        .dash-card-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: #32325d;
          margin: 0;
        }
        .dash-card-subtitle {
          font-size: 0.8rem;
          color: #8898aa;
        }
        .dash-card-body {
          padding: 1.5rem;
        }
      }

      .chart-wrap {
        position: relative;
        height: 320px;
      }

      .table-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
        gap: 1.5rem;
      }

      .dash-table {
        width: 100%;
        border-collapse: collapse;

        thead th {
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #8898aa;
          padding: 0.85rem 1rem;
          background: #f6f9fc;
          border-bottom: 1px solid #e9ecef;
          white-space: nowrap;
        }
        tbody td {
          font-size: 0.85rem;
          color: #525f7f;
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #f1f3f6;
          white-space: nowrap;
        }
        tbody tr:hover td {
          background: #f6f9fc;
        }
        .empty-row {
          text-align: center;
          color: #adb5bd;
          padding: 2rem 1rem;
        }
        .text-right  { text-align: right; }
        .text-center { text-align: center; }
      }
    }
    </style>
    